import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const HELP_ARTICLES = [
  {
    title: 'How Lineage Bible Works',
    description: 'An overview of recording, listening, and connecting with your family.',
    href: 'https://lineagebible.com/help/welcome-to-family-bible-app.html',
  },
  {
    title: 'Recording Your First Bible Passage',
    description: 'Learn how to record a passage and share it with your family.',
    href: 'https://lineagebible.com/help/how-recording-works.html',
  },
  {
    title: 'Listening to Family Recordings',
    description: 'Follow along with a Bible plan and hear your family read aloud.',
    href: 'https://lineagebible.com/help/how-listening-works.html',
  },
  {
    title: 'Setting Up Your Profile',
    description: 'Add a photo and name so your family knows who you are.',
    href: 'https://lineagebible.com/help/creating-your-account-and-profile.html',
  },
  {
    title: 'Notification Preferences',
    description: 'Choose when and how you get notified about new recordings.',
    href: 'https://lineagebible.com/help/notification-settings.html',
  },
];

export default function AwaitingApproval() {
  const { currentUser, logout, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (currentUser?.familyIds?.length > 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, navigate]);

  const familyName = currentUser?.pendingFamilyName || 'your family';
  const familyId = currentUser?.pendingFamilyId;

  const handleResend = async () => {
    if (!familyId || resending) return;
    setResending(true);
    try {
      await setDoc(doc(db, 'families', familyId, 'requests', currentUser.uid), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        displayName: currentUser.displayName || currentUser.email,
        photoURL: currentUser.photoURL || '',
        status: 'pending',
        createdAt: new Date().toISOString(),
      });
      setResent(true);
      setTimeout(() => setResent(false), 3000);
    } catch (err) {
      console.error('Error resending request:', err);
    } finally {
      setResending(false);
    }
  };

  const handleCheckApproval = async () => {
    await refreshUserData();
  };

  const handleCancel = async () => {
    if (!familyId || cancelling) return;
    setCancelling(true);
    try {
      await updateDoc(doc(db, 'families', familyId, 'requests', currentUser.uid), {
        status: 'cancelled',
      });
      await updateDoc(doc(db, 'users', currentUser.uid), {
        pendingFamilyId: deleteField(),
        pendingFamilyName: deleteField(),
      });
      await refreshUserData();
      navigate('/onboard?intent=join', { replace: true });
    } catch (err) {
      console.error('Error cancelling request:', err);
      setCancelling(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try { await logout(); } catch (_) { setSigningOut(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-space-cadet via-space-cadet/90 to-cerulean/80 flex items-start sm:items-center justify-center pt-8 pb-16 sm:py-8 px-4 overflow-y-auto">
      <div
        className="fixed inset-0 z-0 bg-no-repeat bg-cover bg-center opacity-10"
        style={{ backgroundImage: "url('/images/family-bible-app2.jpg')", filter: 'grayscale(100%)' }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8">

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/images/familyBibleApp-logo.png"
              alt="Lineage Bible"
              className="h-20 w-auto"
            />
          </div>

          {/* Status */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-space-cadet">Awaiting Approval</h1>
            <p className="text-gray-500 mt-2 text-sm leading-relaxed">
              Your request to join <strong className="text-space-cadet">{familyName}</strong> has been sent.
              A family admin needs to approve it before you can access the app.
            </p>
          </div>

          {/* Actions */}
          <div className="space-y-3 mb-8">
            <button
              onClick={handleResend}
              disabled={resending || resent}
              className="w-full bg-gradient-to-r from-cerulean to-aqua-deep text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {resent ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Request Resent!
                </>
              ) : resending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Resending…
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Resend Approval Request
                </>
              )}
            </button>

            <button
              onClick={handleCheckApproval}
              className="w-full py-2.5 text-sm text-cerulean hover:text-cerulean/80 font-medium transition-colors"
            >
              Check if I've been approved
            </button>

            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              {cancelling ? 'Cancelling…' : 'Cancel request and try a different family'}
            </button>
          </div>

          {/* Help Articles */}
          <div className="border-t border-gray-100 pt-6">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              While You Wait — Learn More
            </p>
            <div className="space-y-1">
              {HELP_ARTICLES.map((article) => (
                <a
                  key={article.href}
                  href={article.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
                >
                  <div className="w-8 h-8 bg-cerulean/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-cerulean/20 transition-colors">
                    <svg className="w-4 h-4 text-cerulean" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-space-cadet group-hover:text-cerulean transition-colors">{article.title}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{article.description}</div>
                  </div>
                  <svg className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1 group-hover:text-cerulean transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Sign out */}
        <div className="text-center mt-4">
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="text-white/50 hover:text-white/80 text-sm transition-colors"
          >
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </div>
    </div>
  );
}
